import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { Panel } from "@/components/ui/Panel";
import { DataTable } from "@/components/ui/DataTable";
import { useToast } from "@/features/toast/ToastProvider";
import { useConfirm } from "@/features/confirm/ConfirmProvider";
import { formatMoney } from "@bookboost/shared/format";

interface ProductRow {
  product_id: string;
  rule_label: string | null;
  rule_commissionable: boolean | null;
  rule_is_onboarding: boolean;
  rule_notes: string | null;
  sample_description: string | null;
  invoice_count: number;
  total_amount_minor: number;
  currency: string;
}

type FilterKey = "all" | "commissionable" | "excluded" | "unset" | "onboarding";

export const Route = createFileRoute("/admin/products")({
  component: ProductsPage,
});

function ProductsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  const [filter, setFilter] = useState<FilterKey>("unset");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "products"],
    queryFn: () => api.get<{ products: ProductRow[] }>("/api/admin/products"),
  });

  const products = data?.products ?? [];

  const counts = useMemo(() => {
    const c = { all: products.length, commissionable: 0, excluded: 0, unset: 0, onboarding: 0 };
    for (const p of products) {
      if (p.rule_commissionable === true) c.commissionable++;
      else if (p.rule_commissionable === false) c.excluded++;
      else c.unset++;
      if (p.rule_is_onboarding) c.onboarding++;
    }
    return c;
  }, [products]);

  const filtered = useMemo(() => {
    switch (filter) {
      case "commissionable":
        return products.filter((p) => p.rule_commissionable === true);
      case "excluded":
        return products.filter((p) => p.rule_commissionable === false);
      case "unset":
        return products.filter((p) => p.rule_commissionable === null);
      case "onboarding":
        return products.filter((p) => p.rule_is_onboarding);
      default:
        return products;
    }
  }, [products, filter]);

  const setRule = useMutation({
    mutationFn: ({ id, commissionable }: { id: string; commissionable: boolean | null }) =>
      api.put(`/api/admin/products/${encodeURIComponent(id)}/rule`, { commissionable }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "products"] }),
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't save."),
  });

  const setOnboarding = useMutation({
    mutationFn: ({ id, isOnboarding }: { id: string; isOnboarding: boolean }) =>
      api.put(`/api/admin/products/${encodeURIComponent(id)}/onboarding`, { is_onboarding: isOnboarding }),
    onSuccess: (_, vars) => {
      toast.ok(vars.isOnboarding ? "Marked as onboarding." : "Unmarked as onboarding.");
      qc.invalidateQueries({ queryKey: ["admin", "products"] });
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't toggle."),
  });

  const bulkRule = useMutation({
    mutationFn: ({ ids, commissionable }: { ids: string[]; commissionable: boolean | null }) =>
      api.post<{ ok: true; updated: number }>("/api/admin/products/bulk-rule", {
        product_ids: ids,
        commissionable,
      }),
    onSuccess: (res, vars) => {
      toast.ok(`Marked ${res.updated} product${res.updated === 1 ? "" : "s"} as ${vars.commissionable === true ? "commissionable" : vars.commissionable === false ? "excluded" : "unset"}.`);
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["admin", "products"] });
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't bulk save."),
  });

  const recompute = useMutation({
    mutationFn: () =>
      api.post<{ scanned: number; flipped: number; trueCount: number; falseCount: number; reviewCount: number; onboardingCount: number }>(
        "/api/admin/products/recompute",
      ),
    onSuccess: (res) => {
      toast.ok(
        `Scanned ${res.scanned} invoices, flipped ${res.flipped}. Now ${res.trueCount} commissionable / ${res.falseCount} excluded / ${res.reviewCount} review / ${res.onboardingCount} onboarding.`,
        7500,
      );
      qc.invalidateQueries({ queryKey: ["admin"] });
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Recompute failed."),
  });

  const visibleIds = filtered.map((p) => p.product_id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someSelected = visibleIds.some((id) => selected.has(id));

  function toggleAll(checked: boolean) {
    setSelected(checked ? new Set(visibleIds) : new Set());
  }
  function toggleOne(id: string, checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  async function onRecompute() {
    const ok = await confirm({
      title: "Recompute all invoices",
      prompt:
        "Re-run commissionable + onboarding decisions across every invoice using the current product rules. Safe to run repeatedly.",
      confirmLabel: "Recompute",
    });
    if (ok) recompute.mutate();
  }

  if (isLoading) return <p className="lede">Loading products…</p>;
  if (error || !data) {
    return (
      <Panel variant="err">
        <h2>Products unavailable</h2>
        <p>{error instanceof Error ? error.message : "Failed to load."}</p>
      </Panel>
    );
  }

  return (
    <>
      <h1>Stripe product rules</h1>

      <Panel title="How this works">
        <p className="muted">
          Every Stripe product that appears on any invoice is listed below. Mark each as <strong>commissionable</strong>,{" "}
          <strong>excluded</strong>, or leave <strong>unset</strong> (falls back to <code>billing_reason</code>:{" "}
          <code>subscription_create</code> = commissionable, <code>subscription_cycle/update</code> = not,{" "}
          <code>manual</code> = review-required). The <strong>★ onboarding</strong> flag is independent — it tags a
          product as onboarding so the dashboard knows an onboarding invoice has been raised.
        </p>
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button
            type="button"
            className="primary"
            disabled={recompute.isPending}
            onClick={onRecompute}
          >
            {recompute.isPending ? "Recomputing…" : "Recompute all invoices"}
          </button>
        </div>
      </Panel>

      <Panel padding="tight">
        <div style={{ display: "flex", gap: 14, padding: "10px 14px", borderBottom: "1px solid var(--hairline)" }}>
          <FilterButton current={filter} target="unset" label="Unset" count={counts.unset} onChange={setFilter} />
          <FilterButton current={filter} target="commissionable" label="Commissionable" count={counts.commissionable} onChange={setFilter} />
          <FilterButton current={filter} target="excluded" label="Excluded" count={counts.excluded} onChange={setFilter} />
          <FilterButton current={filter} target="onboarding" label="★ Onboarding" count={counts.onboarding} onChange={setFilter} />
          <FilterButton current={filter} target="all" label="All" count={counts.all} onChange={setFilter} />
          <div style={{ flex: 1 }} />
          {someSelected && (
            <>
              <button
                className="primary"
                style={{ padding: "4px 12px", fontSize: 12 }}
                disabled={bulkRule.isPending}
                onClick={() => bulkRule.mutate({ ids: [...selected], commissionable: true })}
              >
                ✓ Mark {selected.size} commissionable
              </button>
              <button
                style={{ padding: "4px 12px", fontSize: 12 }}
                disabled={bulkRule.isPending}
                onClick={() => bulkRule.mutate({ ids: [...selected], commissionable: false })}
              >
                ✗ Mark {selected.size} excluded
              </button>
            </>
          )}
        </div>

        {filtered.length === 0 ? (
          <p className="lede" style={{ padding: 32, textAlign: "center" }}>
            Nothing in this bucket.
          </p>
        ) : (
          <DataTable>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 32 }}>
                    <input
                      type="checkbox"
                      aria-label="Select all visible"
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = !allSelected && someSelected;
                      }}
                      onChange={(e) => toggleAll(e.target.checked)}
                    />
                  </th>
                  <th>Product</th>
                  <th>Sample description</th>
                  <th>Invoices</th>
                  <th>Total</th>
                  <th>Rule</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.product_id}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Select ${p.product_id}`}
                        checked={selected.has(p.product_id)}
                        onChange={(e) => toggleOne(p.product_id, e.target.checked)}
                      />
                    </td>
                    <td><code style={{ fontSize: 11 }}>{p.product_id}</code></td>
                    <td style={{ maxWidth: 380, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {p.sample_description ?? "—"}
                    </td>
                    <td>{p.invoice_count}</td>
                    <td>{formatMoney(p.total_amount_minor, p.currency)}</td>
                    <td>
                      {p.rule_commissionable === true && <span className="badge earned">commissionable</span>}
                      {p.rule_commissionable === false && <span className="badge">excluded</span>}
                      {p.rule_commissionable === null && <span className="badge pending">unset</span>}
                      {p.rule_is_onboarding && <span className="badge approved" style={{ marginLeft: 4 }}>★ onboarding</span>}
                    </td>
                    <td>
                      <div style={{ display: "inline-flex", gap: 6 }}>
                        <button
                          className="primary"
                          style={{ padding: "4px 8px", fontSize: 12 }}
                          disabled={setRule.isPending || p.rule_commissionable === true}
                          onClick={() => setRule.mutate({ id: p.product_id, commissionable: true })}
                        >
                          ✓ on
                        </button>
                        <button
                          style={{ padding: "4px 8px", fontSize: 12 }}
                          disabled={setRule.isPending || p.rule_commissionable === false}
                          onClick={() => setRule.mutate({ id: p.product_id, commissionable: false })}
                        >
                          ✗ off
                        </button>
                        {p.rule_commissionable !== null && (
                          <button
                            style={{ padding: "4px 8px", fontSize: 12 }}
                            disabled={setRule.isPending}
                            onClick={() => setRule.mutate({ id: p.product_id, commissionable: null })}
                          >
                            reset
                          </button>
                        )}
                        <button
                          style={{
                            padding: "4px 8px",
                            fontSize: 12,
                            background: p.rule_is_onboarding ? "var(--primary50)" : undefined,
                            color: p.rule_is_onboarding ? "var(--accent-deep)" : undefined,
                            borderColor: p.rule_is_onboarding ? "var(--primary300)" : undefined,
                          }}
                          disabled={setOnboarding.isPending}
                          onClick={() => setOnboarding.mutate({ id: p.product_id, isOnboarding: !p.rule_is_onboarding })}
                          title={p.rule_is_onboarding ? "Unmark as onboarding" : "Mark as onboarding"}
                        >
                          ★
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DataTable>
        )}
      </Panel>
    </>
  );
}

function FilterButton({
  current,
  target,
  label,
  count,
  onChange,
}: {
  current: FilterKey;
  target: FilterKey;
  label: string;
  count: number;
  onChange: (key: FilterKey) => void;
}) {
  const active = current === target;
  return (
    <button
      type="button"
      onClick={() => onChange(target)}
      style={{
        padding: "4px 10px",
        fontSize: 12,
        fontWeight: active ? 600 : 500,
        background: active ? "var(--primary50)" : "transparent",
        border: "1px solid",
        borderColor: active ? "var(--primary300)" : "var(--hairline)",
        color: active ? "var(--accent-deep)" : "var(--muted)",
        borderRadius: 999,
      }}
    >
      {label} <span style={{ opacity: 0.7 }}>· {count}</span>
    </button>
  );
}
